/**
 * The widget's colour rule, copied from the reference storefront (`apps/storefront`,
 * `features/chat/lib/contrast.ts`) and the standalone widget: black or white text, whichever
 * reads better on the store's colour. Apps cannot import each other; keep the three in step
 * (ADR 88).
 */
function channel(value: number): number {
  const c = value / 255;
  return c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of `#RRGGBB`, or null for anything else. */
export function luminance(hex: string): number | null {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!match) return null;
  const [r, g, b] = [match[1], match[2], match[3]].map((part) =>
    channel(Number.parseInt(part ?? '0', 16)),
  );
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

export function contrastRatio(a: number, b: number): number {
  const [light, dark] = a > b ? [a, b] : [b, a];
  return (light + 0.05) / (dark + 0.05);
}

/** Black or white, whichever reads better on `hex`; null when `hex` is not a colour. */
export function readableOn(hex: string): '#000000' | '#ffffff' | null {
  const l = luminance(hex);
  if (l === null) return null;
  return contrastRatio(l, 0) >= contrastRatio(l, 1) ? '#000000' : '#ffffff';
}
